import React from 'react';
import ToolTipBody from 'shared/ToolTip/components/ToolTipBody';
import {
  PricingBreakdown,
  LocalizedLabel,
  BreakdownItem,
} from 'contracts/utils/fetchPricingPreview';

// PLA-1034/1035: ícono (i) al lado de "Price Simulator" que abre el desglose del precio que
// devolvió el API (fórmula, componentes por sección y precio final). Los labels ya vienen
// localizados desde el simulador ({ en, es }); acá solo se elige el del idioma actual.
interface Props {
  breakdown: PricingBreakdown;
  // PLA-1092: separa el (i) del texto cuando va al lado de un valor (vista de aprobación).
  classNames?: Array<string>;
}

const t = (key: string): string => I18n.translate(`contracts.price_simulator.${key}`);

const label = (l?: LocalizedLabel): string => (l ? l[I18n.locale] ?? l.en ?? '' : '');

const usd = (value?: number): string => `$${Number(value ?? 0).toFixed(4)}`;

const itemValue = (item: BreakdownItem): string =>
  item.local
    ? `${item.local.value} ${item.local.currency}/${item.local.unit} (${usd(
        item.usdPerLb
      )} USD/lb)`
    : usd(item.usdPerLb);

export function PriceBreakdownTooltip({ breakdown, classNames }: Props) {
  const { formula, formula_with_values: formulaWithValues, sections, total } = breakdown;

  const component = (): JSX.Element => (
    <>
      <span>{t('breakdown_title')}</span>
      <div className='price-breakdown'>
        {formula && (
          <div className='price-breakdown__block'>
            <div className='price-breakdown__label'>{t('breakdown_formula')}</div>
            <div className='price-breakdown__value'>{formula}</div>
          </div>
        )}

        {formulaWithValues && (
          <div className='price-breakdown__block'>
            <div className='price-breakdown__label'>{t('breakdown_formula_values')}</div>
            <div className='price-breakdown__value'>{formulaWithValues}</div>
          </div>
        )}

        {sections && sections.length > 0 && (
          <div className='price-breakdown__block'>
            <div className='price-breakdown__label'>{t('breakdown_components')}</div>
            {sections.map((section) => (
              <div key={section.key} className='price-breakdown__section'>
                <div className='price-breakdown__section-title'>
                  {label(section.label)}
                  {section.subtotalUsdPerLb != null &&
                    ` (${usd(section.subtotalUsdPerLb)} USD/lb)`}
                </div>
                {section.items.map((item) => (
                  <div key={item.key} className='price-breakdown__item'>
                    <span className='price-breakdown__item-label'>
                      {label(item.label)}:
                    </span>{' '}
                    {itemValue(item)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {total && (
          <div className='price-breakdown__total'>
            <span>{label(total.label)}</span>
            <span>{total.formatted}</span>
          </div>
        )}
      </div>
    </>
  );

  return <ToolTipBody component={component} classNames={classNames} top />;
}

export default PriceBreakdownTooltip;
